import { useCallback, useEffect, useMemo, useState, useRef } from "react"
import { useAsyncSelector } from "src/hooks/useAsyncSelector"
import { useRefresher } from "src/hooks/useRefresher"
import { trashboxContactsRequestIdState } from "src/recoil/atoms/contact/trashboxContactsRequestIdState"
import { selectedMyOrganizationSelector } from "src/recoil/selectors/organization/selectedMyOrganizationSelector"
import { useContactDetailDataRefresher } from "src/recoil/hooks/refresher/useContactDetailDataRefresher"
import { useRequestData } from "src/recoil/hooks/useRequestData"
import { useFetcher } from "src/hooks/useFetcher"
import { CustomAlert } from "src/utils/CustomAlert"
import { deleteContactTrashbox } from "src/apis/contact/deleteContactTrashbox"
import { myProfileSelectorFamily } from "src/recoil/selectors/organization/myProfile/myProfileSelectorFamily"
import { restoreContact } from "src/apis/contact/restoreContact"
import { updateFavoriteContactInbox } from "src/apis/contact/updateFavoriteContactInbox"
import { useRecoilState, useRecoilValue, useResetRecoilState } from "recoil"
import { trashContactFavoriteFlgsCacheState } from "src/recoil/atoms/contact/contactFavoriteFlgsCacheState"
import { wait } from "src/utils/wait"
import { trashboxContactsConcatSelectorFamily } from "src/recoil/selectors/contact/trashboxContactsConcatSelectorFamily"
import { trashboxContactsConcatRequestIdState } from "src/recoil/atoms/contact/trashboxContactsConcatRequestIdState"
import { organizationDetailSelectorFamily } from "src/recoil/selectors/organization/organizationDetailSelectorFamily"
import { organizationRefreshState } from "src/recoil/atoms/organization/organizationRefreshPermissionState"
import { organizationDetailRequestIdState } from "src/recoil/atoms/organization/organizationDetailRequestIdState"
import { ContactListCacheService } from "src/services/cache/ContactListCacheService"
import { inboxContactsRequestIdState } from "src/recoil/atoms/contact/inboxContactsRequestIdState"
import { useFocusEffect } from "@react-navigation/native"
import { useBottomTabBadgeData } from "src/recoil/hooks/bottomTab/useBottomTabBadgeData"
import { UnreadContactCountCacheService } from "src/services/cache/UnreadContactCountCacheService"

export const useTrashboxData = () => {
  const { value: selectedOrganization } = useAsyncSelector(selectedMyOrganizationSelector)
  const organizationId = useMemo(() => selectedOrganization?.id, [selectedOrganization])
  const { refreshUnreadContactCount } = useBottomTabBadgeData()
  const { value: organization } = useAsyncSelector(organizationDetailSelectorFamily(organizationId))
  const { value: myProfile } = useAsyncSelector(myProfileSelectorFamily(organizationId))
  const myMemberId = useMemo(() => myProfile?.memberId, [myProfile])
  const refreshContactDetailData = useContactDetailDataRefresher()
  const [nextTokens, setNextTokens] = useState<string[]>([])
  // 組織切り替え時のリフレッシュ用（前回の組織IDを保持）
  const prevOrganizationIdRef = useRef<string | undefined>(undefined)
  // 組織切り替えが発生したかのフラグ（リフレッシュ関数が定義された後に実行するため）
  const [needsOrganizationSwitchRefresh, setNeedsOrganizationSwitchRefresh] = useState(false)
  useEffect(() => {
    setNextTokens([])
    // 組織が変わった場合（初回のundefined→idは除く）
    if (prevOrganizationIdRef.current !== undefined && prevOrganizationIdRef.current !== organizationId) {
      console.log("[useTrashboxData] 組織切り替えを検出、セレクタを再評価します", {
        from: prevOrganizationIdRef.current,
        to: organizationId,
      })
      // リフレッシュ関数が定義された後に実行するためフラグをセット
      setNeedsOrganizationSwitchRefresh(true)
    }
    prevOrganizationIdRef.current = organizationId
  }, [organizationId, setNextTokens])
  const { value: contacts, isLoading: isLoadingContacts } = useAsyncSelector(
    trashboxContactsConcatSelectorFamily({ nextTokens, organizationId }) // 組織切り替え時にセレクタを再評価させるため
  )
  const _refreshContacts = useRefresher(trashboxContactsRequestIdState)
  const _refreshContactsConcat = useRefresher(trashboxContactsConcatRequestIdState)
  const refreshOrganizationDetail = useRefresher(organizationDetailRequestIdState)
  const refreshInboxContacts = useRefresher(inboxContactsRequestIdState)
  const needRefreshOrganization = useRecoilValue(organizationRefreshState)
  useEffect(() => {
    if (needRefreshOrganization) {
      refreshOrganizationDetail()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needRefreshOrganization])

  // 組織切り替え時にRecoilセレクタを強制的に再評価
  useEffect(() => {
    if (needsOrganizationSwitchRefresh) {
      console.log("[useTrashboxData] 組織切り替え後のセレクタ再評価を実行")
      _refreshContacts()
      _refreshContactsConcat()
      setNeedsOrganizationSwitchRefresh(false)
    }
  }, [needsOrganizationSwitchRefresh, _refreshContacts, _refreshContactsConcat])

  const refreshContacts = useCallback(() => {
    _refreshContacts()
    _refreshContactsConcat()
    setNextTokens([])
    refreshOrganizationDetail()
  }, [_refreshContacts, _refreshContactsConcat, refreshOrganizationDetail])

  // 画面フォーカス時に連絡一覧を再取得（Web版SPA対応）
  // ゴミ箱への移動/復元操作後に即座に反映させるため
  useFocusEffect(
    useCallback(() => {
      console.log("[useTrashboxData] 画面フォーカス時に連絡一覧を再取得します")
      refreshContacts()
    }, [refreshContacts])
  )

  const [favoriteFlgsCache, setFavoriteFlgsCache] = useRecoilState(trashContactFavoriteFlgsCacheState)
  const resetFavoriteFlgsCache = useResetRecoilState(trashContactFavoriteFlgsCacheState)

  const requestDataResult = useRequestData()

  const { fetch: execDeleteContact } = useFetcher(
    useCallback(
      async (contactId: string) => {
        if (!requestDataResult.isOk) {
          return
        }
        const { accessToken } = requestDataResult.content
        if (accessToken == null || myMemberId == null) {
          return
        }
        const isConfirmed = await CustomAlert.confirm("確認", "この連絡を完全に削除しますか？\n削除後は復元できません。")
        if (!isConfirmed) {
          return
        }
        const result = await deleteContactTrashbox({ accessToken, myMemberId, contactId })
        if (result.isOk) {
          await CustomAlert.alert("完了", "連絡を完全に削除しました。")
          await wait(2000)
          refreshContacts()
          resetFavoriteFlgsCache()
        } else {
          await CustomAlert.alert("完了", result.error.message)
        }
      },
      [requestDataResult, refreshContacts, myMemberId, resetFavoriteFlgsCache]
    )
  )

  const { fetch: execRestoreContact } = useFetcher(
    useCallback(
      async (contactId: string) => {
        if (!requestDataResult.isOk) {
          return
        }
        const { accessToken } = requestDataResult.content
        if (accessToken == null || myMemberId == null) {
          return
        }
        const isConfirmed = await CustomAlert.confirm("確認", "この連絡を復元しますか?")
        if (!isConfirmed) {
          return
        }

        // 復元する連絡が未読かどうかを確認（バッジ更新用）
        const targetContact = contacts?.contacts?.find((c) => c.id === contactId)
        const wasUnread = targetContact != null && !targetContact.isRead

        const result = await restoreContact({ accessToken, myMemberId, contactId })
        if (result.isOk) {
          // 受信トレイのキャッシュを無効化し、再取得をトリガー
          if (myMemberId) {
            ContactListCacheService.invalidateContainer(myMemberId, "inbox")
            // ゴミ箱のキャッシュも無効化（復元した連絡を一覧から即座に削除）
            ContactListCacheService.invalidateContainer(myMemberId, "trashbox")
          }

          // 未読連絡を復元した場合、未読バッジを楽観的に+1する
          // Note: APIから再取得するとインデックス更新が間に合わない場合があるため、楽観的更新を行う
          if (wasUnread && organizationId) {
            UnreadContactCountCacheService.incrementOptimistically(organizationId, 1)
            refreshUnreadContactCount()
          }

          await CustomAlert.alert("完了", "連絡を受信トレイに移動しました。")
          await wait(2000)

          // SPAの場合、キャッシュ無効化だけでは画面が再描画されないため、
          // 受信トレイの再取得を明示的にトリガーする
          refreshInboxContacts()

          // ゴミ箱の連絡一覧も更新
          refreshContacts()
          resetFavoriteFlgsCache()
        } else {
          await CustomAlert.alert("エラー", result.error.message)
        }
      },
      [
        requestDataResult,
        refreshContacts,
        myMemberId,
        resetFavoriteFlgsCache,
        refreshInboxContacts,
        contacts?.contacts,
        refreshUnreadContactCount,
        organizationId,
      ]
    )
  )

  const { fetch: execUpdateFavoriteContact } = useFetcher(
    useCallback(
      async ({ contactId, isFavorite }: { contactId: string; isFavorite: boolean }) => {
        if (!requestDataResult.isOk) {
          return
        }
        const { accountId, accessToken } = requestDataResult.content
        if (accountId == null || accessToken == null || myMemberId == null) {
          return
        }
        const result = await updateFavoriteContactInbox({ accessToken, myMemberId, contactId, isFavorite })
        if (!result.isOk) {
          CustomAlert.alert("エラー", result.error.message)
          return
        }
        setFavoriteFlgsCache((prev) => ({ ...prev, [contactId]: isFavorite }))
      },
      [requestDataResult, myMemberId, setFavoriteFlgsCache]
    )
  )

  return {
    contacts,
    isLoadingContacts,
    refreshContacts,
    organizationId,
    refreshContactDetailData,
    execDeleteContact,
    execRestoreContact,
    execUpdateFavoriteContact,
    favoriteFlgsCache,
    setNextTokens,
    organization,
  }
}
